import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { humanize } from 'underscore.string';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@cargomatic/ui';
import CargomaticDatePicker from 'components/v2/CargomaticDatePicker';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import moment from 'moment';
import { sluggify } from 'utils/localization/utils';
import { LocaleContext } from 'providers/locale-settings-context';
import PropTypes from 'prop-types';

/* Add whatever additional date needed to the whitelist */
const DATE_WHITE_LIST = [
  'last_free_date',
  'arrival_date',
  'last_cut_date',
  'dispatch_date',
  'discharge_date',
  'earliest_empty_date',
  'empty_returned_date',
  'cfs_gate_in',
  'cfs_gate_out',
];

function getMomentDate(date) {
  if (!date) {
    return null;
  }
  return moment(date);
}

export default function ShipmentDateModal({
  field,
  currentFieldValue = '',
  shipment,
  onSuccess,
  onFail,
  onHide,
  show,
}) {
  const localeContext = useContext(LocaleContext);
  const dateFormat = localeContext?.localeData?.dateFormat || 'MM/DD/YYYY';

  const [selectedDateField, setSelectedDateField] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const momentValue = currentFieldValue
      ? getMomentDate(currentFieldValue)
      : null;
    setSelectedDate(momentValue && momentValue.isValid() ? momentValue : '');
    setSelectedDateField(field);
  }, [field, show, currentFieldValue]);

  const verifyDateField = () => DATE_WHITE_LIST.includes(field.toLowerCase());

  const handleDateChange = (date) => {
    setSelectedDate(date ? moment(date) : '');
  };

  const isValidDate = () => {
    return selectedDate && getMomentDate(selectedDate).isValid();
  };

  const onCancelDateSave = () => {
    setSelectedDate('');
    setSelectedDateField('');
    onHide();
  };

  const onAddDateClick = async () => {
    try {
      setIsSaving(true);
      await ShipmentsActionCreators.updateShipmentActionDates(
        shipment,
        selectedDateField,
        selectedDate,
      );
      setIsSaving(false);
      onSuccess();
    } catch (e) {
      console.log('Failed to add shipment date', e.message);
      setIsSaving(false);
      onFail(e);
    }
  };

  const fieldLabel = humanize(t(sluggify(field)));
  const modalTitle = `${t('PROVIDE')} ${fieldLabel}`;

  if (!selectedDateField || !verifyDateField()) {
    return null;
  }

  const handleOpenChange = (open) => {
    if (!open && !isSaving) onCancelDateSave();
  };

  return (
    <Dialog open={!!show} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="CargomaticModal-510fdb09"
        className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0"
        onInteractOutside={(e) => isSaving && e.preventDefault()}
        onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
      >
        <DialogHeader className="border-b px-6 py-4 flex-none">
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div data-testid="div-27731577" className="space-y-2">
            <Label data-testid="Label-afd16b60">{fieldLabel}</Label>
            <CargomaticDatePicker
              dataTestId="DatePicker-b72f9de8"
              placeholderText={dateFormat}
              dateFormat={dateFormat}
              selected={selectedDate}
              onChange={handleDateChange}
              isClearable
            />
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4 flex-none">
          <Button
            variant="outline"
            disabled={isSaving}
            onClick={onCancelDateSave}
            data-testid="Button-f8b122b3"
          >
            {t('CANCEL')}
          </Button>
          <Button
            disabled={isSaving || !isValidDate()}
            onClick={onAddDateClick}
            data-testid="Button-2ac3f5bb"
          >
            {t('ADD_DATE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ShipmentDateModal.propTypes = {
  field: PropTypes.string.isRequired,
  currentFieldValue: PropTypes.string,
  shipment: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onFail: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
};
